import { useCompany } from "@/contexts/CompanyContext";
import DashboardLayout from "@/components/DashboardLayout";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  CheckCircle2,
  XCircle,
  ArrowRight,
  RefreshCw,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export default function Reconciliation() {
  return (
    <DashboardLayout>
      <ReconciliationContent />
    </DashboardLayout>
  );
}

function ReconciliationContent() {
  const { selectedCompanyId } = useCompany();
  const [selectedBankTx, setSelectedBankTx] = useState<number | null>(null);
  const [selectedSystemTx, setSelectedSystemTx] = useState<number | null>(null);

  const {
    data: unmatchedData,
    isLoading,
    refetch,
  } = trpc.reconciliation.getUnmatched.useQuery(
    { companyId: selectedCompanyId! },
    { enabled: !!selectedCompanyId }
  );

  const autoMatchMutation = trpc.reconciliation.autoMatch.useMutation({
    onSuccess: data => {
      toast.success(`Automatically matched ${data.matchCount} transactions`);
      refetch();
      setSelectedBankTx(null);
      setSelectedSystemTx(null);
    },
    onError: error => {
      toast.error(`Auto-match failed: ${error.message}`);
    },
  });

  const manualMatchMutation = trpc.reconciliation.manualMatch.useMutation({
    onSuccess: () => {
      toast.success("Transactions matched successfully");
      refetch();
      setSelectedBankTx(null);
      setSelectedSystemTx(null);
    },
    onError: error => {
      toast.error(`Manual match failed: ${error.message}`);
    },
  });

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat("de-CH", {
      style: "currency",
      currency: "CHF",
    }).format(amount / 100);
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString("de-CH");
  };

  const handleAutoMatch = () => {
    if (!selectedCompanyId) return;
    autoMatchMutation.mutate({ companyId: selectedCompanyId });
  };

  const handleManualMatch = () => {
    if (!selectedCompanyId || !selectedBankTx || !selectedSystemTx) {
      toast.error(
        "Please select both a bank transaction and a system transaction"
      );
      return;
    }

    manualMatchMutation.mutate({
      companyId: selectedCompanyId,
      bankTransactionId: selectedBankTx,
      systemTransactionId: selectedSystemTx,
    });
  };

  if (!selectedCompanyId) {
    return (
      <div className="container py-8">
        <Card>
          <CardHeader>
            <CardTitle>No Company Selected</CardTitle>
            <CardDescription>
              Please select a company from the company switcher to view
              reconciliation.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="container py-8 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const bankTransactions = unmatchedData?.bankTransactions || [];
  const systemTransactions = unmatchedData?.systemTransactions || [];

  return (
    <div className="container py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Reconciliation</h1>
          <p className="text-muted-foreground mt-2">
            Match bank transactions with system transactions
          </p>
          <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
            <Badge variant="outline" className="font-normal">
              Date Tolerance: ±7 days
            </Badge>
            <span className="text-xs">
              Exact: 30pts | ±1 day: 25pts | ±3 days: 20pts | ±7 days: 15pts
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={handleAutoMatch}
            disabled={
              autoMatchMutation.isPending || bankTransactions.length === 0
            }
          >
            {autoMatchMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle2 className="h-4 w-4 mr-2" />
            )}
            Auto-Match
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">
              Unmatched Bank Transactions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{bankTransactions.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">
              Unmatched System Transactions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {systemTransactions.length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">
              Selected for Matching
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {selectedBankTx && selectedSystemTx
                ? "2"
                : selectedBankTx || selectedSystemTx
                  ? "1"
                  : "0"}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Manual Match Button */}
      {selectedBankTx && selectedSystemTx && (
        <Card className="border-primary">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Badge variant="secondary">
                  Bank Transaction #{selectedBankTx}
                </Badge>
                <ArrowRight className="h-4 w-4" />
                <Badge variant="secondary">
                  System Transaction #{selectedSystemTx}
                </Badge>
              </div>
              <Button
                onClick={handleManualMatch}
                disabled={manualMatchMutation.isPending}
              >
                {manualMatchMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                )}
                Confirm Match
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Side-by-Side Comparison */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Bank Transactions */}
        <Card>
          <CardHeader>
            <CardTitle>Bank Transactions</CardTitle>
            <CardDescription>
              Select a bank transaction to match
            </CardDescription>
          </CardHeader>
          <CardContent>
            {bankTransactions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-success" />
                <p>All bank transactions have been matched!</p>
              </div>
            ) : (
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bankTransactions.map(tx => (
                      <TableRow
                        key={tx.id}
                        className={`cursor-pointer ${
                          selectedBankTx === tx.id
                            ? "bg-primary/10"
                            : "hover:bg-muted/50"
                        }`}
                        onClick={() => setSelectedBankTx(tx.id)}
                      >
                        <TableCell className="font-medium">
                          {formatDate(tx.date)}
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate">
                          {tx.description}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {formatAmount(tx.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* System Transactions */}
        <Card>
          <CardHeader>
            <CardTitle>System Transactions</CardTitle>
            <CardDescription>
              Select a system transaction to match
            </CardDescription>
          </CardHeader>
          <CardContent>
            {systemTransactions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <XCircle className="h-12 w-12 mx-auto mb-4 text-warning" />
                <p>No unmatched system transactions found</p>
              </div>
            ) : (
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {systemTransactions.map(tx => (
                      <TableRow
                        key={tx.id}
                        className={`cursor-pointer ${
                          selectedSystemTx === tx.id
                            ? "bg-primary/10"
                            : "hover:bg-muted/50"
                        }`}
                        onClick={() => setSelectedSystemTx(tx.id)}
                      >
                        <TableCell className="font-medium">
                          {formatDate(tx.date)}
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate">
                          {tx.description}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {formatAmount(tx.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
